/**
 * The page somebody reads after connecting an assistant — or before deciding to.
 *
 * Settings → Integrations answers "how do I connect this?" for the one person
 * with owner rights who went looking for it. This answers the question everyone
 * else has, which is "what do I say to it?", and it is deliberately reachable
 * without any of that: no company in the path, no sign-in, no membership. That
 * makes it a link an owner can send to an estimator who will never open
 * Settings in their life, and it is why the copy here is about the product
 * rather than about any one tenant's data.
 *
 * It renders `@dantria/core`'s capability list rather than prose of its own,
 * so this page, the Settings panel, and the assistant's own answer to "what can
 * you do?" cannot drift apart — see the note at the top of `mcp-guide.ts`.
 */
import { MCP_CAPABILITIES, MCP_EXAMPLES, MCP_LIMITS, MCP_STARTERS } from "@dantria/core";
import { useState } from "react";

/** Read from the browser rather than hard-coded, for the same reason the
 *  Settings panel does it: a staging deployment has to print its own address,
 *  or somebody follows these instructions into production. */
const serverUrl = () => `${window.location.origin}/api/mcp`;

function CopyLine({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-stretch gap-2">
      <p className="min-w-0 flex-1 rounded-lg bg-white px-3 py-2 text-neutral-800 text-sm ring-1 ring-neutral-200">
        {value}
      </p>
      <button
        type="button"
        className="shrink-0 rounded-lg px-3 py-2 font-medium text-neutral-600 text-sm ring-1 ring-neutral-200 hover:bg-neutral-50"
        onClick={() => {
          navigator.clipboard?.writeText(value).then(
            () => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            },
            () => undefined,
          );
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="font-semibold text-lg text-neutral-900">{title}</h2>
      {children}
    </section>
  );
}

export function AssistantGuide() {
  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-3xl space-y-10 px-6 py-12">
        <header className="space-y-3">
          <p className="font-medium text-neutral-500 text-sm uppercase tracking-wide">Dantria</p>
          <h1 className="font-semibold text-3xl text-neutral-900">
            Quoting by dictation, against your own prices
          </h1>
          <p className="text-lg text-neutral-600">
            Connect Claude or ChatGPT and you can price a job out loud, have the proposal written
            from your own rate book, and get back a link the client can open. Here's what it can do
            and how to ask for it.
          </p>
        </header>

        <Section title="What it can do">
          <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {MCP_CAPABILITIES.map((capability) => (
              <div key={capability.what}>
                <dt className="font-medium text-neutral-900">{capability.what}</dt>
                <dd className="text-neutral-600 text-sm">{capability.detail}</dd>
              </div>
            ))}
          </dl>
        </Section>

        <Section title="What it can't do">
          {/* Stated as plainly as the capabilities. Somebody weighing whether to
              hand out a credential is asking this question at least as hard as
              the other one, and a list that answers only half of it reads as
              marketing. */}
          <ul className="space-y-2">
            {MCP_LIMITS.map((limit) => (
              <li key={limit} className="flex gap-2 text-neutral-600 text-sm">
                <span aria-hidden className="text-neutral-400">
                  —
                </span>
                <span>{limit}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Things to say">
          <p className="text-neutral-600 text-sm">
            Say the whole thing in one go — the client, the measurements, how it bills. There's no
            need to break it into steps, and it will read your price book before it quotes anything.
          </p>
          <ul className="space-y-4">
            {MCP_EXAMPLES.map((example) => (
              <li key={example.text} className="space-y-1">
                <p className="text-neutral-500 text-xs uppercase tracking-wide">{example.about}</p>
                <CopyLine value={example.text} />
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Or pick a starter">
          <p className="text-neutral-600 text-sm">
            Your assistant lists these on the Dantria connection. Each one walks a whole job in the
            right order, so you don't have to remember it.
          </p>
          <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {MCP_STARTERS.map((starter) => (
              <div key={starter.name}>
                <dt className="font-medium text-neutral-900">{starter.title}</dt>
                <dd className="text-neutral-600 text-sm">{starter.detail}</dd>
              </div>
            ))}
          </dl>
        </Section>

        <Section title="Connecting">
          <p className="text-neutral-600 text-sm">
            In Claude or ChatGPT, open connector settings and paste this address. Nothing else — no
            key, no client ID. You'll be sent here to sign in and pick which company it may act on.
          </p>
          <CopyLine value={serverUrl()} />
          <p className="text-neutral-500 text-sm">
            Only an owner can approve a connection. They'll find the rest, including keys for Claude
            Code, under Settings → Integrations.
          </p>
        </Section>
      </div>
    </div>
  );
}
